// Copyright (C) 2026 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

// ImageJ's Brightness/Contrast histogram, for the same reason ImageJ shows one: on a fluorescence
// frame the Min/Max sliders are meaningless without seeing where the data actually sits. Almost
// every sample is in the low background peak, the signal is a long thin tail, and a window picked
// blind either clips the cells away or shows a black rectangle.
//
// The samples come from whichever source is on screen: the decoded 16-bit frame when there is one
// (its real camera counts, 0..2047 or whatever the sensor gives), otherwise the 8-bit background
// canvas, read back BEFORE the CSS filter -- getImageData returns the buffer, and the Min/Max
// windowing is a paint-time filter over it, so what we measure is the unwindowed frame either way.

import React, {
    useCallback, useEffect, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import Text from 'antd/lib/typography/Text';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox';

import { DisplayCurve, applyCurve, currentGray16 } from 'utils/sixteen-bit';
import { CombinedState } from 'reducers';

// One bar per 2 CSS pixels of a ~260px wide plot: finer than that and the bars stop being
// readable, coarser and a narrow window has nothing to sit between.
const BINS = 128;
const PLOT_HEIGHT = 74;
const RAMP_HEIGHT = 8;

interface Histogram {
    counts: Uint32Array;
    peak: number;
    total: number;
    dataMin: number;
    dataMax: number;
    mode: number;
}

interface Props {
    minLevel: number;
    maxLevel: number;
    levelLimit: number;
    curve: DisplayCurve;
    curveParameter: number;
}

function summarise(
    counts: Uint32Array, total: number, dataMin: number, dataMax: number, levelLimit: number,
): Histogram {
    const scale = (levelLimit + 1) / BINS;
    let peak = 0;
    let mode = 0;
    for (let bin = 0; bin < BINS; bin++) {
        if (counts[bin] > peak) {
            peak = counts[bin];
            mode = Math.round((bin + 0.5) * scale);
        }
    }
    return {
        counts,
        peak,
        total,
        dataMin: total ? dataMin : 0,
        dataMax: total ? dataMax : 0,
        mode,
    };
}

function fromGray16(levelLimit: number): Histogram | null {
    const source = currentGray16();
    if (!source) return null;
    const counts = new Uint32Array(BINS);
    const { data } = source;
    // Bin index by multiplication rather than a divide per sample: this runs over every pixel of
    // a 1280x1280 frame and is on the path of every frame change while the panel is open.
    const toBin = BINS / (levelLimit + 1);
    const last = BINS - 1;
    let dataMin = 65535;
    let dataMax = 0;
    for (let i = 0; i < data.length; i++) {
        const value = data[i];
        if (value < dataMin) dataMin = value;
        if (value > dataMax) dataMax = value;
        const bin = Math.trunc(value * toBin);
        counts[bin > last ? last : bin] += 1;
    }
    return summarise(counts, data.length, dataMin, dataMax, levelLimit);
}

function fromCanvas(canvasInstance: unknown, levelLimit: number): Histogram | null {
    const wrapper = (canvasInstance && typeof (canvasInstance as any).html === 'function' ?
        (canvasInstance as any).html() : null) as HTMLElement | null;
    const background = (wrapper?.querySelector('canvas#cvat_canvas_background') ??
        wrapper?.querySelector('canvas')) as HTMLCanvasElement | null;
    if (!background || !background.width || !background.height) return null;
    const context = background.getContext('2d');
    if (!context) return null;
    let pixels: Uint8ClampedArray;
    try {
        pixels = context.getImageData(0, 0, background.width, background.height).data;
    } catch {
        // A cross-origin frame taints the canvas. Nothing to show, and nothing broken either.
        return null;
    }

    // Subsample big frames: a full 4K readback is 8M samples per frame change, and the shape of a
    // histogram with a quarter of a million samples in it is indistinguishable at this size.
    const pixelCount = pixels.length / 4;
    const stride = Math.max(1, Math.floor(pixelCount / 250000));
    const counts = new Uint32Array(BINS);
    const toBin = BINS / (levelLimit + 1);
    const last = BINS - 1;
    let total = 0;
    let dataMin = 255;
    let dataMax = 0;
    for (let p = 0; p < pixelCount; p += stride) {
        const i = p * 4;
        // Greyscale frames put the same value in all three channels; on a colour one this is the
        // luminance ImageJ plots for RGB.
        const value = Math.trunc(pixels[i] * 0.299 + pixels[i + 1] * 0.587 + pixels[i + 2] * 0.114);
        if (value < dataMin) dataMin = value;
        if (value > dataMax) dataMax = value;
        const bin = Math.trunc(value * toBin);
        counts[bin > last ? last : bin] += 1;
        total += 1;
    }
    return summarise(counts, total, dataMin, dataMax, levelLimit);
}

export default function ImageHistogram(props: Props): JSX.Element {
    const {
        minLevel, maxLevel, levelLimit, curve, curveParameter,
    } = props;
    const canvasInstance = useSelector((state: CombinedState) => state.annotation.canvas.instance);
    const frame = useSelector((state: CombinedState) => state.annotation.player.frame.number);
    const imageFilters = useSelector((state: CombinedState) => state.settings.imageFilters);

    const containerRef = useRef<HTMLDivElement>(null);
    const plotRef = useRef<HTMLCanvasElement>(null);
    const measuredRef = useRef<unknown>(null);
    const [histogram, setHistogram] = useState<Histogram | null>(null);
    const [logScale, setLogScale] = useState(false);

    const measure = useCallback((): void => {
        const container = containerRef.current;
        // The dropdown keeps this mounted after the first open, so a hidden panel would otherwise
        // read back the whole frame on every step through the job for nobody to look at.
        if (!container || !container.offsetWidth) return;

        // Dragging Min/Max rebuilds a 16-bit frame, so canvas.setup fires on every tick of the
        // slider -- but only the WINDOW moved, the samples under it are the same ones. Keyed on
        // what the histogram is actually a picture of, so a drag rebins nothing.
        // The decoded frame is identity-keyed -- loadGray16 hands back the same object for a
        // frame already in its cache -- and the 8-bit canvas by the frame it is showing.
        const source = currentGray16();
        const key = source ?? `${levelLimit}:${frame}`;
        if (measuredRef.current === key && histogram) return;

        const measured = source ? fromGray16(levelLimit) : fromCanvas(canvasInstance, levelLimit);
        // A null measurement means the frame is not on the canvas yet; leaving the key unset lets
        // the next canvas.setup try again rather than freezing on an empty plot.
        if (measured) measuredRef.current = key;
        setHistogram(measured);
    }, [canvasInstance, levelLimit, frame, histogram, imageFilters]);

    // The frame number changes before the image is decoded, and on a 16-bit task the samples
    // arrive a fetch later still, so the frame number alone is too early to measure on --
    // canvas.setup is the point at which what we would measure is what is on screen.
    useEffect(() => {
        // An OpenCV filter rewrites the pixels the 8-bit path reads back, so the cached
        // measurement no longer describes what is on the canvas.
        measuredRef.current = null;
    }, [imageFilters]);

    useEffect(() => {
        measure();
        const wrapper = (canvasInstance && typeof (canvasInstance as any).html === 'function' ?
            (canvasInstance as any).html() : null) as HTMLElement | null;
        if (!wrapper) return () => {};
        wrapper.addEventListener('canvas.setup', measure);
        return () => wrapper.removeEventListener('canvas.setup', measure);
    }, [measure, frame]);

    // Reopening the panel makes the container non-zero again; there is no event for that, and the
    // frame may well have moved on while it was closed.
    useEffect(() => {
        const container = containerRef.current;
        if (!container || typeof ResizeObserver === 'undefined') return () => {};
        let hadWidth = container.offsetWidth > 0;
        const observer = new ResizeObserver(() => {
            const hasWidth = (containerRef.current?.offsetWidth ?? 0) > 0;
            if (hasWidth && !hadWidth) measure();
            hadWidth = hasWidth;
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, [measure]);

    useEffect(() => {
        const plot = plotRef.current;
        const container = containerRef.current;
        if (!plot || !container) return;
        const width = container.offsetWidth || 260;
        const ratio = window.devicePixelRatio || 1;
        plot.width = Math.round(width * ratio);
        plot.height = Math.round((PLOT_HEIGHT + RAMP_HEIGHT) * ratio);
        plot.style.width = `${width}px`;
        plot.style.height = `${PLOT_HEIGHT + RAMP_HEIGHT}px`;
        const context = plot.getContext('2d');
        if (!context) return;
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        context.clearRect(0, 0, width, PLOT_HEIGHT + RAMP_HEIGHT);

        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, PLOT_HEIGHT);

        if (histogram && histogram.peak) {
            const { counts, peak } = histogram;
            const barWidth = width / BINS;
            const ceiling = logScale ? Math.log1p(peak) : peak;
            const binScale = (levelLimit + 1) / BINS;
            for (let bin = 0; bin < BINS; bin++) {
                const count = counts[bin];
                if (!count) continue;
                const height = ((logScale ? Math.log1p(count) : count) / ceiling) * (PLOT_HEIGHT - 2);
                const centre = (bin + 0.5) * binScale;
                // Inside the window the bars are the part of the data that survives to the screen;
                // outside they are what is being clipped to pure black or pure white.
                context.fillStyle = centre >= minLevel && centre <= maxLevel ? '#595959' : '#d0d0d0';
                context.fillRect(bin * barWidth, PLOT_HEIGHT - height, Math.max(barWidth - 0.5, 0.5), height);
            }
        } else {
            context.fillStyle = '#bfbfbf';
            context.font = '11px sans-serif';
            context.fillText('no image data', 6, PLOT_HEIGHT / 2);
        }

        // ImageJ's display-range line: black below Min, the curve across the window, white above
        // Max. It is the transfer function the sliders are actually editing, sampled per pixel of
        // the plot so a gamma or asinh curve is drawn as the curve it is.
        const span = Math.max(maxLevel - minLevel, 1e-9);
        const screenAt = (x: number): number => applyCurve(
            ((x / width) * Math.max(levelLimit, 1) - minLevel) / span, curve, curveParameter,
        );
        context.strokeStyle = '#1890ff';
        context.lineWidth = 1;
        context.beginPath();
        for (let x = 0; x <= width; x++) {
            const y = PLOT_HEIGHT - 0.5 - screenAt(x) * (PLOT_HEIGHT - 1);
            if (x === 0) context.moveTo(x, y); else context.lineTo(x, y);
        }
        context.stroke();

        // The resulting greyscale ramp, so the window can be read as the picture it produces.
        for (let x = 0; x < width; x++) {
            const grey = Math.round(screenAt(x + 0.5) * 255);
            context.fillStyle = `rgb(${grey},${grey},${grey})`;
            context.fillRect(x, PLOT_HEIGHT, 1, RAMP_HEIGHT);
        }
        context.strokeStyle = '#bfbfbf';
        context.strokeRect(0.5, 0.5, width - 1, PLOT_HEIGHT + RAMP_HEIGHT - 1);
    }, [histogram, logScale, minLevel, maxLevel, levelLimit, curve, curveParameter]);

    return (
        <div className='cvat-image-setups-histogram' ref={containerRef}>
            <canvas ref={plotRef} />
            <div className='cvat-image-setups-histogram-stats'>
                <Text type='secondary'>
                    {histogram ? (
                        `n=${histogram.total} min=${histogram.dataMin} max=${histogram.dataMax} mode=${histogram.mode}`
                    ) : `0..${levelLimit}`}
                </Text>
                <Checkbox
                    checked={logScale}
                    onChange={(event: CheckboxChangeEvent): void => setLogScale(event.target.checked)}
                >
                    <Text type='secondary'>Log</Text>
                </Checkbox>
            </div>
        </div>
    );
}
